/**
 * Per-row serializer for Master Price List full-document replaces.
 *
 * The caller owns the row (`readRow` / `writeRow`). Patches are merged into
 * that row immediately; the PUT is built from it at send time. There is no
 * second copy of the row. A newer edit that lands during an in-flight replace
 * is not overwritten by the stale response — the merged row is sent next.
 * Different item ids have independent chains.
 */
export function createMasterPriceListRowQueue({
  readRow,
  writeRow,
  saveRow,
  refreshRow,
  onError,
}) {
  const chains = new Map();
  const revisions = new Map();

  async function restoreRow(itemId, revision) {
    try {
      const fresh = await refreshRow(itemId);
      if (revisions.get(itemId) !== revision) return;
      if (fresh) writeRow(itemId, fresh);
    } catch (error) {
      console.error('Failed to refresh master price list item after update:', error);
    }
  }

  async function flush(itemId, revision) {
    if (revisions.get(itemId) !== revision) return;
    const row = readRow(itemId);
    if (!row) return;
    try {
      await saveRow(itemId, row);
    } catch (error) {
      if (typeof onError === 'function') onError(error);
      if (revisions.get(itemId) !== revision) return;
      await restoreRow(itemId, revision);
      return;
    }
    if (revisions.get(itemId) !== revision) return;
    await restoreRow(itemId, revision);
  }

  return {
    apply(itemId, patch) {
      const current = readRow(itemId);
      if (!current) return null;
      writeRow(itemId, { ...current, ...patch });
      const revision = (revisions.get(itemId) || 0) + 1;
      revisions.set(itemId, revision);
      const previous = chains.get(itemId) || Promise.resolve();
      const job = previous.catch(() => {}).then(() => flush(itemId, revision));
      const tracked = job.finally(() => {
        if (chains.get(itemId) === tracked) chains.delete(itemId);
      });
      chains.set(itemId, tracked);
      return tracked;
    },
  };
}
